import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  compileLoomControlEdit,
  deriveLoomParameterControls,
  formatLoomControlPath,
  type LoomParameterControl,
} from "./loomParameterControls";
import type {
  LoomAuthoringCommand,
  LoomAuthoringDocument,
} from "./loomAuthoringModel";
import { createLoomCommandId, type LoomCommandAcceptance } from "./loomAuthoringHistory";
import type {
  LoomAuthoringPreviewRequest,
  LoomNativeAnalyticJacobianParameterId,
} from "./useLoomAuthoringHistory";

interface LoomAutoParameterControlsProps {
  document: LoomAuthoringDocument;
  nodeId: string;
  onCommand: (command: LoomAuthoringCommand, acceptance?: LoomCommandAcceptance) => string | null;
  onPreviewCancel?: () => void;
  onPreviewCommand?: (command: LoomAuthoringCommand, request: LoomAuthoringPreviewRequest) => string | null;
  pinnedControlIds?: readonly string[];
  taskGroups?: boolean;
}

function unitLabel(control: LoomParameterControl): string {
  return control.unit === "boolean" || control.unit === "enum"
    ? control.label
    : `${control.label} (${control.unit})`;
}

function NumericControl({
  control,
  onCancelPreview,
  onEdit,
  onPreview,
}: {
  control: LoomParameterControl & { editor: Extract<LoomParameterControl["editor"], { kind: "range" }> };
  onCancelPreview: () => void;
  onEdit: (control: LoomParameterControl, value: number) => void;
  onPreview: (control: LoomParameterControl, value: number) => void;
}) {
  const value = typeof control.value === "number" ? control.value : 0;
  const [draft, setDraft] = useState(value);
  const [textDraft, setTextDraft] = useState(String(value));
  const draftRef = useRef(value);
  const dirtyRef = useRef(false);
  const committedRef = useRef(false);
  useEffect(() => {
    draftRef.current = value;
    dirtyRef.current = false;
    committedRef.current = false;
    setDraft(value);
    setTextDraft(String(value));
  }, [value]);
  useEffect(() => () => onCancelPreview(), [onCancelPreview]);
  const preview = (next: number) => {
    draftRef.current = next;
    dirtyRef.current = true;
    committedRef.current = false;
    setDraft(next);
    setTextDraft(String(next));
    if (Number.isFinite(next)) onPreview(control, next);
  };
  const commit = () => {
    if (committedRef.current || !dirtyRef.current) return;
    committedRef.current = true;
    dirtyRef.current = false;
    const next = draftRef.current;
    onCancelPreview();
    if (Number.isFinite(next) && next !== value) onEdit(control, next);
    else {
      draftRef.current = value;
      setDraft(value);
      setTextDraft(String(value));
    }
  };
  const cancel = () => {
    committedRef.current = true;
    dirtyRef.current = false;
    draftRef.current = value;
    setDraft(value);
    setTextDraft(String(value));
    onCancelPreview();
  };
  const accessibleLabel = unitLabel(control);
  const validNumericDraft = (next: number) => Number.isFinite(next)
    && (control.unit !== "count" || Number.isInteger(next))
    && (control.editor.hardMin === undefined || (control.editor.exclusiveMin
      ? next > control.editor.hardMin
      : next >= control.editor.hardMin))
    && (control.editor.hardMax === undefined || next <= control.editor.hardMax);
  const display = control.editor.precision === 0
    ? Math.round(draft).toString()
    : draft.toFixed(Math.min(control.editor.precision, 4));
  const localSweepPreview = nativeAnalyticParameter(control) === "sweep_deg";
  return (
    <div className="loom-generated-number">
      <div><span>{control.label}</span><output>{display} {control.unit}</output></div>
      <input
        aria-label={accessibleLabel}
        max={control.editor.uiMax}
        min={control.editor.uiMin}
        onBlur={commit}
        onInput={(event) => preview(Number(event.currentTarget.value))}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            cancel();
          }
        }}
        onKeyUp={(event) => {
          if (event.key.startsWith("Arrow") || event.key === "PageUp" || event.key === "PageDown" || event.key === "Home" || event.key === "End") commit();
        }}
        onPointerCancel={cancel}
        onPointerDown={() => {
          dirtyRef.current = false;
          committedRef.current = false;
        }}
        onPointerUp={commit}
        step={control.editor.step}
        type="range"
        value={draft}
      />
      <input
        aria-label={`${accessibleLabel} value`}
        inputMode="decimal"
        max={control.editor.hardMax}
        min={control.editor.hardMin}
        onBlur={commit}
        onChange={(event) => {
          const nextText = event.currentTarget.value;
          const next = event.currentTarget.valueAsNumber;
          setTextDraft(nextText);
          committedRef.current = false;
          if (!validNumericDraft(next)) {
            draftRef.current = Number.NaN;
            dirtyRef.current = true;
            setDraft(value);
            onCancelPreview();
            return;
          }
          draftRef.current = next;
          dirtyRef.current = true;
          setDraft(next);
          onPreview(control, next);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            cancel();
          } else if (event.key === "Enter") event.currentTarget.blur();
        }}
        step={control.editor.step}
        type="number"
        value={textDraft}
      />
      {localSweepPreview ? <small data-testid="loom-local-sweep-preview-window">Release to update 3D</small> : null}
    </div>
  );
}

function nativeAnalyticParameter(control: LoomParameterControl): LoomNativeAnalyticJacobianParameterId | null {
  if (control.locator.nodeId !== "wing") return null;
  const path = formatLoomControlPath(control.locator.path);
  const matches: Readonly<Record<string, LoomNativeAnalyticJacobianParameterId>> = {
    "panels[panel-1].sweepDeg": "sweep_deg",
    "panels[panel-1].chordRootM": "chord_root",
    "panels[panel-1].chordTipM": "taper",
    "panels[panel-1].yTipM": "half_span",
    "panels[panel-1].twistTipDeg": "twist_tip_deg",
    "panels[panel-1].dihedralDeg": "dihedral_deg",
  };
  return matches[path] ?? null;
}

function taskGroup(control: LoomParameterControl): "Airfoils" | "Planform" | "Position" {
  const path = formatLoomControlPath(control.locator.path);
  if (/\.airfoil(?:Root|Tip)\./.test(path)) return "Airfoils";
  if (/\.(?:dihedralDeg|twistRootDeg|twistTipDeg|xOffsetM|zOffsetM)$/.test(path)) {
    return "Position";
  }
  return "Planform";
}

function ControlGroup({
  children,
  count,
  forceOpen,
  initiallyOpen,
  label,
}: {
  children: React.ReactNode;
  count: number;
  forceOpen: boolean;
  initiallyOpen: boolean;
  label: string;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);
  return (
    <details data-control-group={label} data-testid="loom-parameter-group" onToggle={(event) => setOpen(event.currentTarget.open)} open={open}>
      <summary><span>{label}</span><em>{count}</em></summary>
      <div>{children}</div>
    </details>
  );
}

export function LoomAutoParameterControls({
  document,
  nodeId,
  onCommand,
  onPreviewCancel,
  onPreviewCommand,
  pinnedControlIds = [],
  taskGroups = false,
}: LoomAutoParameterControlsProps) {
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const controls = useMemo(() => deriveLoomParameterControls(document, nodeId), [document, nodeId]);
  const node = document.nodes.find((candidate) => candidate.id === nodeId);
  const groups = useMemo(() => {
    const values = new Map<string, LoomParameterControl[]>();
    for (const control of controls) {
      const group = taskGroups ? taskGroup(control) : control.group;
      values.set(group, [...(values.get(group) ?? []), control]);
    }
    const entries = [...values.entries()];
    const order = ["Planform", "Airfoils", "Position"];
    return taskGroups ? entries.sort(([left], [right]) => (
      order.indexOf(left) - order.indexOf(right)
    )) : entries;
  }, [controls, taskGroups]);
  const pinned = useMemo(() => new Set(pinnedControlIds), [pinnedControlIds]);
  useEffect(() => {
    const target = rootRef.current?.querySelector<HTMLElement>('[data-pinned="true"]');
    if (typeof target?.scrollIntoView === "function") target.scrollIntoView({ block: "nearest" });
  }, [pinnedControlIds]);
  const edit = (control: LoomParameterControl, value: number | boolean | string) => {
    try {
      const command = compileLoomControlEdit(document, { controlId: control.id, value }, {
        commandId: createLoomCommandId("gui-control", document.revision),
        expectedRevision: document.revision,
      });
      const commandError = onCommand(command, { origin: "gui.control", subjectIds: [control.id] });
      setError(commandError);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to edit this Loom parameter");
    }
  };
  const preview = (control: LoomParameterControl, value: number) => {
    if (!onPreviewCommand) return;
    try {
      const command = compileLoomControlEdit(document, { controlId: control.id, value }, {
        commandId: createLoomCommandId("gui-preview", document.revision),
        expectedRevision: document.revision,
      });
      const commandError = onPreviewCommand(command, {
        controlId: control.id,
        backend: "explicit-parametric-regeneration",
        analyticJacobianParameterId: nativeAnalyticParameter(control),
      });
      setError(commandError);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to preview this Loom parameter");
    }
  };
  const cancelPreview = useCallback(() => onPreviewCancel?.(), [onPreviewCancel]);
  return (
    <div
      aria-label={`${node?.label ?? nodeId} parameters`}
      className="loom-parameter-controls"
      data-node-id={nodeId}
      data-schema-control-count={controls.length}
      data-testid="loom-parameter-controls"
      ref={rootRef}
      role="group"
    >
      <div className="loom-parameter-summary"><span>Shape controls</span><strong>{controls.length}{pinned.size ? ` · ${pinned.size} pinned` : ""}</strong></div>
      {groups.map(([group, groupControls], groupIndex) => (
        <ControlGroup
          count={groupControls.length}
          forceOpen={groupControls.some((control) => pinned.has(control.id))}
          initiallyOpen={groupIndex === 0}
          key={group}
          label={group}
        >
            {groupControls.map((control) => (
              <div
                className={`loom-parameter-control${pinned.has(control.id) ? " is-pinned" : ""}`}
                data-affects={control.affects}
                data-node-id={nodeId}
                data-parameter-path={formatLoomControlPath(control.locator.path)}
                data-pinned={pinned.has(control.id)}
                data-preview-analytic-parameter={nativeAnalyticParameter(control) ?? "none"}
                data-testid="loom-parameter-control"
                key={control.id}
              >
                {control.editor.kind === "range" ? (
                  <NumericControl
                    control={control as LoomParameterControl & { editor: typeof control.editor }}
                    onCancelPreview={cancelPreview}
                    onEdit={edit}
                    onPreview={preview}
                  />
                ) : control.editor.kind === "toggle" ? (
                  <label className="loom-generated-toggle"><span>{control.label}</span><input aria-label={unitLabel(control)} checked={Boolean(control.value)} onChange={(event) => edit(control, event.target.checked)} type="checkbox" /></label>
                ) : (
                  <label className="loom-generated-select"><span>{control.label}</span><select aria-label={unitLabel(control)} onChange={(event) => edit(control, event.target.value)} value={String(control.value)}>{control.editor.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                )}
              </div>
            ))}
        </ControlGroup>
      ))}
      {controls.length === 0 ? <small>No parameter controls are declared for this node.</small> : null}
      {error ? <div className="pg-inline-error" role="alert">{error}</div> : null}
    </div>
  );
}
